<template>
  <div class="phone-field">
    <div class="phone-field__row">
      <div class="phone-field__country" :class="inputClass">
        <span class="phone-field__country-text" aria-hidden="true">
          {{ countryText }}
          <i class="fas fa-chevron-down"></i>
        </span>
        <!-- Select nativo invisible encima del texto: Safari oculta el texto de un select con estilos propios -->
        <select
          :id="`${inputId}-country`"
          class="phone-field__country-select"
          :value="country"
          aria-label="Código de país"
          @change="onCountryChange"
        >
          <option v-for="option in countries" :key="option.code" :value="option.code">
            {{ option.label }} ({{ option.dialCode }})
          </option>
        </select>
      </div>
      <input
        :id="inputId"
        :class="inputClass"
        :value="value"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        maxlength="22"
        :placeholder="placeholder"
        :required="required"
        :aria-invalid="error ? 'true' : null"
        :aria-describedby="error ? `${inputId}-error` : null"
        @input="onInput"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
    </div>
    <small v-if="error" :id="`${inputId}-error`" class="phone-field__error" role="alert">{{ error }}</small>
  </div>
</template>

<script>
import {
  PHONE_COUNTRIES,
  detectPhoneCountry,
  formatPhoneAsYouType,
  phoneCountryLabel,
} from '@/core/utils/phone'

/**
 * Teléfono con país, igual que la app móvil: formatea mientras se escribe y detecta el país por el prefijo +.
 * v-model para el número y :country.sync para el país.
 */
export default {
  name: 'PhoneField',
  props: {
    value: { type: String, default: '' },
    country: { type: String, default: 'US' },
    inputId: { type: String, required: true },
    inputClass: { type: [String, Array, Object], default: 'form-control' },
    placeholder: { type: String, default: '(214) 555-0000' },
    required: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  data() {
    return { countries: PHONE_COUNTRIES }
  },
  computed: {
    countryText() {
      return phoneCountryLabel(this.country)
    },
  },
  methods: {
    onInput(event) {
      const raw = event.target.value
      const country = detectPhoneCountry(raw, this.country)
      if (country !== this.country) this.$emit('update:country', country)
      const formatted = formatPhoneAsYouType(raw, country)
      event.target.value = formatted
      this.$emit('input', formatted)
    },
    onCountryChange(event) {
      const country = event.target.value
      this.$emit('update:country', country)
      if (this.value) this.$emit('input', formatPhoneAsYouType(this.value, country))
    },
  },
}
</script>

<style lang="scss" scoped>
.phone-field__row {
  display: flex;
  gap: 8px;

  > input {
    flex: 1;
    min-width: 0;
  }
}

.phone-field__country {
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  width: auto;
  min-width: 104px;
  max-width: 132px;
  cursor: pointer;

  &:focus-within {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
}

.phone-field__country-text {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  i {
    font-size: 0.7em;
    opacity: 0.6;
  }
}

.phone-field__country-select {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}

.phone-field__error {
  display: block;
  margin-top: 6px;
  color: #c4223a;
  font-size: 0.9rem;
  font-weight: 600;
}
</style>
